import {Directive, EventEmitter, HostListener, Input, OnDestroy, Output} from '@angular/core';
import {debounceTime, Subject, takeUntil} from 'rxjs';

/**
 * A reusable Angular directive that debounces the value of an `<input>`
 * element and emits the final value when the user has stopped typing.
 *
 * The directive listens to the native `input` event, pushes the value
 * through an RxJS `Subject`, and emits the value via an `EventEmitter`
 * only after a configurable delay (default 400 ms).
 *
 * ### Usage
 *
 * ```html
 * <input
 *   appInputDebounce
 *   [debounce]="200"                <!-- optional: change the delay -->
 *   (appInputDebounce)="onDone($event)">
 * ```
 *
 * The output event (`appInputDebounce`) will emit the last input value
 * after the user stops typing for the specified delay.
 *
 * ### Why use this?
 *
 * - Avoids making a request on every keystroke.
 * - Keeps the component template clean – all logic lives in the directive.
 * - Works with both template‑driven and reactive forms.
 *
 * @directive
 */
@Directive({
    selector: '[appInputDebounce]'
})
export class InputDebounceDirective implements OnDestroy {
    private readonly _typing$ = new Subject<unknown>();
    private readonly _destroy$ = new Subject<void>();

    // ===============================================================
    // ===============================================================

    /**
     * Emits when the user stops typing for {@link debounce} milliseconds.
     *
     * The event payload is the last value entered in the input.
     *
     * @example
     * ```ts
     * @Component({
     *   template: '<input appInputDebounce (appInputDebounce)="search($event)"/>'
     * })
     * export class SearchComponent {
     *   search(term: string) { ... }
     * }
     * ```
     */
    @Output('appInputDebounce')
    public finishedTyping = new EventEmitter<unknown>();

    /**
     * The debounce duration in **milliseconds**.
     *
     * It can be bound from the host element:
     *
     * ```html
     * <input appInputDebounce [debounce]="250" …>
     * ```
     *
     * @default 400
     */
    @Input()
    public debounce = 400;

    // ===============================================================
    // ===============================================================

    /**
     * Angular does **not** allow a private constructor on a directive.
     *
     * The constructor must be `public` so that Angular can instantiate
     * the directive.  In the original snippet a `private constructor()` was
     * declared, which would prevent Angular from creating the directive
     * and would result in an error at runtime.
     *
     * **Fix:** simply change `private constructor()` → `constructor()`.
     *
     * The constructor sets up the debounced subscription that feeds the
     * output event emitter.
     */
    constructor() {
        // Push incoming values into `typing$`, debounce them, then emit.
        this._typing$
            .pipe(debounceTime(this.debounce), takeUntil(this._destroy$))
            .subscribe((value) => this.finishedTyping.emit(value));
    }
    /**
     * Angular lifecycle hook called just before the directive is destroyed.
     *
     * It triggers the cleanup of the RxJS streams (`destroy$` is marked
     * completed) so that no stray subscriptions remain after the host
     * component is removed from the DOM.
     */
    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Listens to the native `input` event on the host element.
     *
     * The `$event.target.value` is passed through `typing$`.  Because
     * the value is emitted immediately into the stream, the debounce logic
     * runs independently of the component code.
     *
     * @param value The new value from the input element.
     */
    @HostListener('input', ['$event.target'])
    public onInput(target: EventTarget | null): void {
        if (!target) return;

        const value = (target as HTMLInputElement).value;
        this._typing$.next(value);
    }
}
